/**
 * Incident Data & Analytics Module
 * 
 * Enterprise operations dashboard for retailer alert incident intelligence.
 * Tabs: Dashboard | Incident Log | Analytics | Safety Scores
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { incidentAPI, hotelsAPI, hotelGroupsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Checkbox } from '../../components/ui/checkbox';
import CommandBar from '../../components/CommandBar';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend,
} from 'recharts';
import {
  Activity, AlertTriangle, ArrowLeft, ArrowUpDown, Building2, Calendar,
  Check, ChevronDown, ChevronLeft, ChevronRight, Download, Eye, Filter,
  FolderOpen, Loader2, MapPin, Search, Shield, ShieldAlert, TrendingUp,
  X, RefreshCw, FileText,
} from 'lucide-react';

const PRIORITY_COLORS = { HIGH: '#ef4444', MEDIUM: '#f97316', LOW: '#22c55e' };
const PRIORITY_LABELS = { HIGH: 'Red', MEDIUM: 'Orange', LOW: 'Green' };
const PIE_COLORS = ['#ef4444', '#f97316', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6', '#eab308'];

// =============================================
// SUMMARY CARDS
// =============================================
const MetricCard = ({ label, value, icon: Icon, color, sub }) => (
  <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,22%)]" data-testid={`metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-2xl font-bold" style={{ color: color || '#f8fafc' }}>{value}</p>
          <p className="text-xs text-[hsl(215,20%,55%)] mt-0.5">{label}</p>
          {sub && <p className="text-[10px] text-[hsl(215,20%,45%)]">{sub}</p>}
        </div>
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color || '#3b82f6'}15` }}>
          <Icon className="w-5 h-5" style={{ color: color || '#3b82f6' }} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const PriorityBadge = ({ priority }) => {
  const map = { HIGH: { bg: 'bg-red-500/15', text: 'text-red-400', label: 'High' }, MEDIUM: { bg: 'bg-orange-500/15', text: 'text-orange-400', label: 'Medium' }, LOW: { bg: 'bg-green-500/15', text: 'text-green-400', label: 'Low' } };
  const s = map[priority] || map.MEDIUM;
  return <Badge variant="outline" className={`${s.bg} ${s.text} border-transparent text-xs`}>{s.label}</Badge>;
};

const StatusBadge = ({ status }) => {
  const open = status === 'OPEN';
  return <Badge variant="outline" className={`${open ? 'bg-amber-500/15 text-amber-400' : 'bg-slate-500/15 text-slate-400'} border-transparent text-xs`}>{status}</Badge>;
};

// =============================================
// MULTI-SELECT STORE PICKER
// =============================================
function StorePickerMulti({ hotels, groups, selectedIds, onChange }) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const allIds = hotels.map(h => h.id);
  const isAll = selectedIds.length === 0;

  // Group hotels by city/region
  const regionMap = useMemo(() => {
    const map = {};
    hotels.forEach(h => {
      const region = h.city || h.address || 'Other';
      if (!map[region]) map[region] = [];
      map[region].push(h);
    });
    return map;
  }, [hotels]);

  const regions = Object.keys(regionMap).sort();

  const filtered = searchTerm
    ? hotels.filter(h => h.name?.toLowerCase().includes(searchTerm.toLowerCase()) || h.city?.toLowerCase().includes(searchTerm.toLowerCase()))
    : null;

  const toggleStore = (id) => {
    if (isAll) {
      onChange(allIds.filter(i => i !== id));
    } else if (selectedIds.includes(id)) {
      const next = selectedIds.filter(i => i !== id);
      onChange(next.length === 0 ? [] : next);
    } else {
      const next = [...selectedIds, id];
      onChange(next.length === allIds.length ? [] : next);
    }
  };

  const selectAll = () => onChange([]);

  const selectRegion = (region) => {
    const regionIds = regionMap[region].map(h => h.id);
    const allSelected = regionIds.every(id => isAll || selectedIds.includes(id));
    if (allSelected) {
      if (isAll) {
        onChange(allIds.filter(id => !regionIds.includes(id)));
      } else {
        const next = selectedIds.filter(id => !regionIds.includes(id));
        onChange(next.length === 0 ? [] : next);
      }
    } else {
      if (isAll) {
        onChange(regionIds);
      } else {
        const next = [...new Set([...selectedIds, ...regionIds])];
        onChange(next.length === allIds.length ? [] : next);
      }
    }
  };

  const selectGroup = (group) => {
    const gIds = group.hotel_ids.filter(id => allIds.includes(id));
    const allSelected = gIds.every(id => isAll || selectedIds.includes(id));
    if (allSelected) {
      if (isAll) {
        onChange(allIds.filter(id => !gIds.includes(id)));
      } else {
        const next = selectedIds.filter(id => !gIds.includes(id));
        onChange(next.length === 0 ? [] : next);
      }
    } else {
      if (isAll) {
        onChange(gIds);
      } else {
        const next = [...new Set([...selectedIds, ...gIds])];
        onChange(next.length === allIds.length ? [] : next);
      }
    }
  };

  const label = isAll
    ? `All Stores (${allIds.length})`
    : selectedIds.length === 1
      ? hotels.find(h => h.id === selectedIds[0])?.name || '1 Store'
      : `${selectedIds.length} Stores Selected`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)] text-[hsl(215,20%,65%)] hover:text-white min-w-[200px] justify-between"
          data-testid="store-picker"
        >
          <div className="flex items-center space-x-1.5 truncate">
            <Building2 className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">{label}</span>
          </div>
          <ChevronDown className="w-3.5 h-3.5 flex-shrink-0 ml-1" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0 bg-[hsl(217,19%,10%)] border-[hsl(217,19%,22%)]" align="end">
        {/* Search */}
        <div className="p-2 border-b border-[hsl(217,19%,20%)]">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[hsl(215,20%,40%)]" />
            <Input
              value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search stores..."
              className="pl-7 h-7 text-xs bg-[hsl(217,19%,15%)] border-[hsl(217,19%,25%)]"
              data-testid="store-picker-search"
            />
          </div>
        </div>

        <div className="max-h-[320px] overflow-y-auto p-1.5 space-y-1">
          {/* All Stores */}
          {!searchTerm && (
            <button
              onClick={selectAll}
              className={`w-full flex items-center space-x-2 px-2 py-1.5 rounded text-xs transition-colors ${isAll ? 'bg-blue-600/20 text-blue-400' : 'text-[hsl(215,20%,65%)] hover:bg-[hsl(217,19%,15%)]'}`}
              data-testid="store-picker-all"
            >
              {isAll && <Check className="w-3.5 h-3.5" />}
              <Building2 className="w-3.5 h-3.5" />
              <span className="font-medium">All Stores ({allIds.length})</span>
            </button>
          )}

          {/* Hotel Groups */}
          {!searchTerm && groups.length > 0 && (
            <div className="pt-1">
              <p className="text-[10px] text-[hsl(215,20%,40%)] uppercase tracking-wider px-2 py-1">Groups</p>
              {groups.map(g => {
                const gIds = g.hotel_ids.filter(id => allIds.includes(id));
                const allSel = gIds.length > 0 && gIds.every(id => isAll || selectedIds.includes(id));
                return (
                  <button
                    key={g.id}
                    onClick={() => selectGroup(g)}
                    className={`w-full flex items-center space-x-2 px-2 py-1.5 rounded text-xs transition-colors ${allSel ? 'bg-purple-600/20 text-purple-400' : 'text-[hsl(215,20%,65%)] hover:bg-[hsl(217,19%,15%)]'}`}
                  >
                    <FolderOpen className="w-3.5 h-3.5" />
                    <span>{g.name}</span>
                    <span className="text-[hsl(215,20%,40%)] ml-auto">{gIds.length}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Search results */}
          {searchTerm && filtered && (
            filtered.length === 0 ? (
              <p className="text-xs text-[hsl(215,20%,40%)] text-center py-3">No stores match</p>
            ) : filtered.map(h => {
              const checked = isAll || selectedIds.includes(h.id);
              return (
                <div key={h.id} onClick={() => toggleStore(h.id)}
                  className={`w-full flex items-center space-x-2 px-2 py-1.5 rounded text-xs transition-colors cursor-pointer ${checked ? 'bg-blue-600/10 text-white' : 'text-[hsl(215,20%,65%)] hover:bg-[hsl(217,19%,15%)]'}`}
                >
                  <Checkbox checked={checked} className="h-3.5 w-3.5 pointer-events-none" />
                  <span className="truncate">{h.name}</span>
                  {h.city && <span className="text-[hsl(215,20%,40%)] ml-auto text-[10px]">{h.city}</span>}
                </div>
              );
            })
          )}

          {/* By Region/City */}
          {!searchTerm && regions.map(region => {
            const stores = regionMap[region];
            const regionIds = stores.map(h => h.id);
            const allSel = regionIds.every(id => isAll || selectedIds.includes(id));
            return (
              <div key={region} className="pt-1">
                <div
                  onClick={() => selectRegion(region)}
                  className={`w-full flex items-center space-x-2 px-2 py-1 rounded text-[10px] uppercase tracking-wider transition-colors cursor-pointer ${allSel ? 'text-blue-400' : 'text-[hsl(215,20%,40%)] hover:text-[hsl(215,20%,60%)]'}`}
                >
                  <MapPin className="w-3 h-3" />
                  <span>{region}</span>
                  <span className="ml-auto">{stores.length}</span>
                </div>
                {stores.map(h => {
                  const checked = isAll || selectedIds.includes(h.id);
                  return (
                    <div key={h.id} onClick={() => toggleStore(h.id)}
                      className={`w-full flex items-center space-x-2 px-2 py-1.5 pl-6 rounded text-xs transition-colors cursor-pointer ${checked ? 'bg-blue-600/10 text-white' : 'text-[hsl(215,20%,65%)] hover:bg-[hsl(217,19%,15%)]'}`}
                    >
                      <Checkbox checked={checked} className="h-3.5 w-3.5 pointer-events-none" />
                      <span className="truncate">{h.name}</span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Footer with count */}
        {!isAll && selectedIds.length > 0 && (
          <div className="border-t border-[hsl(217,19%,20%)] p-2 flex items-center justify-between">
            <span className="text-[10px] text-[hsl(215,20%,50%)]">{selectedIds.length} of {allIds.length} selected</span>
            <Button size="sm" variant="ghost" className="h-6 text-[10px] text-blue-400" onClick={selectAll}>
              Clear Selection
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// =============================================
// MAIN COMPONENT
// =============================================
export default function IncidentData() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [hotels, setHotels] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedStoreIds, setSelectedStoreIds] = useState([]);

  // Dashboard state
  const [dashboard, setDashboard] = useState(null);

  // Incident log state
  const [incidents, setIncidents] = useState([]);
  const [logTotal, setLogTotal] = useState(0);
  const [logPage, setLogPage] = useState(1);
  const [logPages, setLogPages] = useState(1);
  const [logSearch, setLogSearch] = useState('');
  const [logPriority, setLogPriority] = useState('all');
  const [logStatus, setLogStatus] = useState('all');
  const [logSort, setLogSort] = useState('incident_created_at');
  const [logOrder, setLogOrder] = useState('desc');
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Safety scores state
  const [safetyScores, setSafetyScores] = useState(null);
  const [safetyDays, setSafetyDays] = useState(30);

  // Derive org_ids param from selection (empty array = all stores)
  const orgIdsParam = selectedStoreIds.length > 0 ? selectedStoreIds.join(',') : undefined;

  // Fetch hotels + groups for store selector
  useEffect(() => {
    const loadData = async () => {
      try {
        const [hotelsRes, groupsRes] = await Promise.all([
          hotelsAPI.list(),
          hotelGroupsAPI.list().catch(() => ({ data: [] })),
        ]);
        setHotels(hotelsRes.data || []);
        setGroups(groupsRes.data || []);
        // Auto-select store from URL param (per-property deep link)
        const storeParam = searchParams.get('store');
        if (storeParam) {
          setSelectedStoreIds([storeParam]);
        }
      } catch (e) { /* ignore */ }
    };
    loadData();
  }, [searchParams]);

  // Fetch dashboard
  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await incidentAPI.getDashboard(orgIdsParam);
      setDashboard(res.data);
    } catch (e) {
      if (e.response?.status === 401) return;
      toast.error('Failed to load dashboard');
    } finally { setLoading(false); }
  }, [orgIdsParam]);

  // Fetch incidents log
  const fetchIncidents = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 25, sort_by: logSort, sort_order: logOrder };
      if (orgIdsParam) params.org_ids = orgIdsParam;
      if (logSearch) params.search = logSearch;
      if (logPriority !== 'all') params.priority = logPriority;
      if (logStatus !== 'all') params.status = logStatus;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await incidentAPI.list(params);
      setIncidents(res.data.incidents || []);
      setLogTotal(res.data.total || 0);
      setLogPage(res.data.page || 1);
      setLogPages(res.data.pages || 1);
    } catch (e) {
      if (e.response?.status === 401) return;
      toast.error('Failed to load incidents');
    } finally { setLoading(false); }
  }, [orgIdsParam, logSearch, logPriority, logStatus, logSort, logOrder, dateFrom, dateTo]);

  // Fetch analytics
  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (orgIdsParam) params.org_ids = orgIdsParam;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await incidentAPI.getAnalytics(params);
      setAnalytics(res.data);
    } catch (e) {
      if (e.response?.status === 401) return;
      toast.error('Failed to load analytics');
    } finally { setLoading(false); }
  }, [orgIdsParam, dateFrom, dateTo]);

  // Fetch safety scores
  const fetchSafetyScores = useCallback(async () => {
    setLoading(true);
    try {
      const res = await incidentAPI.getSafetyScores(orgIdsParam, safetyDays);
      setSafetyScores(res.data);
    } catch (e) {
      if (e.response?.status === 401) return;
      toast.error('Failed to load safety scores');
    } finally { setLoading(false); }
  }, [orgIdsParam, safetyDays]);

  // Auto-fetch on tab change
  useEffect(() => {
    if (activeTab === 'dashboard') fetchDashboard();
    else if (activeTab === 'log') fetchIncidents(1);
    else if (activeTab === 'analytics') fetchAnalytics();
    else if (activeTab === 'safety') fetchSafetyScores();
  }, [activeTab, fetchDashboard, fetchIncidents, fetchAnalytics, fetchSafetyScores]);

  // Backfill trigger
  const handleBackfill = async () => {
    try {
      const res = await incidentAPI.backfill();
      toast.success(res.data.message);
      fetchDashboard();
    } catch (e) {
      toast.error('Backfill failed');
    }
  };

  // CSV Export
  const handleExport = async () => {
    try {
      const params = {};
      if (orgIdsParam) params.org_ids = orgIdsParam;
      if (logPriority !== 'all') params.priority = logPriority;
      if (logStatus !== 'all') params.status = logStatus;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await incidentAPI.exportCSV(params);
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `incidents_export_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (e) {
      toast.error('Export failed');
    }
  };

  // Open incident detail
  const openDetail = async (incidentId) => {
    try {
      const res = await incidentAPI.getDetail(incidentId);
      setSelectedIncident(res.data);
      setDetailOpen(true);
    } catch (e) {
      toast.error('Failed to load incident');
    }
  };

  const handleSort = (col) => {
    if (logSort === col) {
      setLogOrder(o => o === 'asc' ? 'desc' : 'asc');
    } else {
      setLogSort(col);
      setLogOrder('desc');
    }
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: Activity },
    { id: 'log', label: 'Incident Log', icon: FileText },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp },
    { id: 'safety', label: 'Safety Scores', icon: Shield },
  ];

  const actions = (
    <div className="flex items-center space-x-2">
      <Button size="sm" variant="outline" onClick={handleExport} className="border-[hsl(217,19%,27%)] text-[hsl(215,20%,65%)] hover:text-white" data-testid="export-csv-btn">
        <Download className="w-4 h-4 mr-1" /> Export CSV
      </Button>
      <Button size="sm" variant="outline" onClick={() => navigate('/hotels')} className="border-[hsl(217,19%,27%)] text-[hsl(215,20%,65%)] hover:text-white" data-testid="back-to-hotels">
        <ArrowLeft className="w-4 h-4 mr-1" /> Properties
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="incident-data-page">
      <CommandBar title="Incident Data" subtitle="Operations Intelligence & Analytics" actions={actions} />

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Store selector + tabs */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center space-x-1 bg-[hsl(217,19%,10%)] rounded-lg p-1" data-testid="tab-navigation">
            {tabs.map(t => (
              <button
                key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setActiveTab(t.id)}
                className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-sm transition-all ${activeTab === t.id ? 'bg-[hsl(217,19%,20%)] text-white' : 'text-[hsl(215,20%,55%)] hover:text-white'}`}
              >
                <t.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{t.label}</span>
              </button>
            ))}
          </div>
          <div className="flex items-center space-x-2">
            <StorePickerMulti
              hotels={hotels}
              groups={groups}
              selectedIds={selectedStoreIds}
              onChange={setSelectedStoreIds}
            />
          </div>
        </div>

        {/* TAB CONTENT */}
        {activeTab === 'dashboard' && <DashboardTab dashboard={dashboard} loading={loading} onBackfill={handleBackfill} />}
        {activeTab === 'log' && (
          <LogTab
            incidents={incidents} total={logTotal} page={logPage} pages={logPages}
            search={logSearch} setSearch={setLogSearch} priority={logPriority} setPriority={setLogPriority}
            status={logStatus} setStatus={setLogStatus} sort={logSort} order={logOrder}
            onSort={handleSort} onPageChange={(p) => fetchIncidents(p)}
            onOpenDetail={openDetail} loading={loading}
            dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo}
            onApply={() => fetchIncidents(1)}
          />
        )}
        {activeTab === 'analytics' && (
          <AnalyticsTab
            analytics={analytics} loading={loading}
            dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo}
            onApply={fetchAnalytics}
          />
        )}
        {activeTab === 'safety' && <SafetyTab scores={safetyScores} loading={loading} days={safetyDays} setDays={setSafetyDays} onRefresh={fetchSafetyScores} />}
      </main>

      {/* Incident Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg bg-[hsl(217,19%,10%)] border-[hsl(217,19%,22%)]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center space-x-2">
              <ShieldAlert className="w-5 h-5 text-amber-400" />
              <span>Incident Detail</span>
            </DialogTitle>
          </DialogHeader>
          {selectedIncident && <IncidentDetail incident={selectedIncident} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =============================================
// DASHBOARD TAB
// =============================================
function DashboardTab({ dashboard, loading, onBackfill }) {
  if (loading && !dashboard) return <LoadingState />;
  if (!dashboard) return null;

  const d = dashboard;
  const priorityData = (d.incidents_by_priority || []).map(p => ({ name: PRIORITY_LABELS[p.priority] || p.priority, value: p.count, fill: PRIORITY_COLORS[p.priority] || '#666' }));

  return (
    <div className="space-y-6" data-testid="dashboard-tab">

      {/* ========== 1. OPERATIONAL SAFETY METRICS ========== */}
      <div>
        <div className="flex items-center space-x-2 mb-3">
          <ShieldAlert className="w-4 h-4 text-blue-400" />
          <h2 className="text-sm font-semibold text-[hsl(215,20%,75%)] tracking-wide uppercase" data-testid="safety-metrics-heading">Operational Safety Metrics</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <MetricCard label="Incidents Today" value={d.total_today} icon={Activity} color="#3b82f6" />
          <MetricCard label="This Week" value={d.total_week} icon={TrendingUp} color="#8b5cf6" />
          <MetricCard label="Red Alerts" value={d.red_alerts_week} icon={AlertTriangle} color="#ef4444" sub="High Priority" />
          <MetricCard label="Orange Alerts" value={d.orange_alerts_week} icon={AlertTriangle} color="#f97316" sub="Medium Priority" />
          <MetricCard label="Green Alerts" value={d.green_alerts_week} icon={AlertTriangle} color="#22c55e" sub="Low Priority" />
          <MetricCard label="Open Incidents" value={d.open_incidents} icon={ShieldAlert} color="#eab308" />
        </div>
      </div>

      {/* ========== 2. TOP HAZARD LOCATIONS + STORE COMPARISON ========== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Top Hazard Locations — ranked list */}
        <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,22%)]">
          <CardHeader className="pb-2">
            <div className="flex items-center space-x-2">
              <MapPin className="w-4 h-4 text-orange-400" />
              <CardTitle className="text-sm text-[hsl(215,20%,75%)]" data-testid="hazard-locations-heading">Top Hazard Locations</CardTitle>
            </div>
            <p className="text-[10px] text-[hsl(215,20%,45%)] mt-0.5">Locations with highest incident frequency</p>
          </CardHeader>
          <CardContent>
            {d.incidents_by_location?.length > 0 ? (
              <div className="space-y-1.5" data-testid="hazard-locations-list">
                {d.incidents_by_location.slice(0, 8).map((loc, i) => {
                  const maxCount = d.incidents_by_location[0].count;
                  const pct = maxCount > 0 ? (loc.count / maxCount) * 100 : 0;
                  return (
                    <div key={i} className="group" data-testid={`hazard-location-${i}`}>
                      <div className="flex items-center justify-between mb-0.5">
                        <div className="flex items-center space-x-2 flex-1 min-w-0">
                          <span className="w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold bg-[hsl(217,19%,18%)] text-[hsl(215,20%,50%)]">{i + 1}</span>
                          <span className="text-xs text-white truncate">{loc.location}</span>
                        </div>
                        <span className="text-xs font-semibold text-orange-400 ml-2">{loc.count} incidents</span>
                      </div>
                      <div className="ml-7 h-1.5 rounded-full bg-[hsl(217,19%,18%)] overflow-hidden">
                        <div className="h-full rounded-full bg-orange-500/60 transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : <p className="text-sm text-[hsl(215,20%,40%)] text-center py-6">No location data available</p>}
          </CardContent>
        </Card>

        {/* Store Safety Comparison — ranked list */}
        <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,22%)]">
          <CardHeader className="pb-2">
            <div className="flex items-center space-x-2">
              <Building2 className="w-4 h-4 text-purple-400" />
              <CardTitle className="text-sm text-[hsl(215,20%,75%)]" data-testid="store-comparison-heading">Store Safety Comparison</CardTitle>
            </div>
            <p className="text-[10px] text-[hsl(215,20%,45%)] mt-0.5">Incident activity by store this week</p>
          </CardHeader>
          <CardContent>
            {d.incidents_by_store?.length > 0 ? (
              <div className="space-y-2" data-testid="store-comparison-list">
                {d.incidents_by_store.slice(0, 8).map((store, i) => {
                  const maxCount = d.incidents_by_store[0].count;
                  const pct = maxCount > 0 ? (store.count / maxCount) * 100 : 0;
                  return (
                    <div key={i} data-testid={`store-comparison-${i}`}>
                      <div className="flex items-center justify-between mb-0.5">
                        <div className="flex items-center space-x-2 flex-1 min-w-0">
                          <span className="w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold bg-[hsl(217,19%,18%)] text-[hsl(215,20%,50%)]">{i + 1}</span>
                          <span className="text-xs text-white truncate">{store.store_name}</span>
                        </div>
                        <span className="text-xs font-semibold text-purple-400 ml-2">{store.count} incidents</span>
                      </div>
                      <div className="ml-7 h-1.5 rounded-full bg-[hsl(217,19%,18%)] overflow-hidden">
                        <div className="h-full rounded-full bg-purple-500/60 transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : <p className="text-sm text-[hsl(215,20%,40%)] text-center py-6">No store data available</p>}
          </CardContent>
        </Card>
      </div>

      {/* ========== 3. CATEGORY BREAKDOWN + PRIORITY DISTRIBUTION ========== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,22%)] lg:col-span-2">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-[hsl(215,20%,65%)]">Incidents by Category (This Week)</CardTitle></CardHeader>
          <CardContent className="h-[250px]">
            {d.incidents_by_category?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.incidents_by_category.map((c, i) => ({ ...c, fill: PIE_COLORS[i % PIE_COLORS.length] }))} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(217,19%,22%)" />
                  <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                  <YAxis dataKey="category" type="category" width={150} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: 'hsl(217,19%,15%)', border: '1px solid hsl(217,19%,27%)', color: '#f8fafc', fontSize: 12 }} />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {d.incidents_by_category.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyChart />}
          </CardContent>
        </Card>

        <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,22%)]">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-[hsl(215,20%,65%)]">Priority Distribution</CardTitle></CardHeader>
          <CardContent className="h-[250px]">
            {priorityData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={priorityData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={3} label={({ name, value }) => `${name}: ${value}`}>
                    {priorityData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: 'hsl(217,19%,15%)', border: '1px solid hsl(217,19%,27%)', color: '#f8fafc', fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <EmptyChart />}
          </CardContent>
        </Card>
      </div>

      {/* ========== 4. LIVE INCIDENT FEED ========== */}
      <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,22%)]">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm text-[hsl(215,20%,65%)]">Live Incident Feed</CardTitle>
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-[10px] text-[hsl(215,20%,50%)]">Live</span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {d.live_feed?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="live-feed-table">
                <thead>
                  <tr className="border-b border-[hsl(217,19%,22%)]">
                    <th className="text-left py-2 px-2 text-[hsl(215,20%,50%)] text-xs font-medium">Time</th>
                    <th className="text-left py-2 px-2 text-[hsl(215,20%,50%)] text-xs font-medium">Store</th>
                    <th className="text-left py-2 px-2 text-[hsl(215,20%,50%)] text-xs font-medium">Location</th>
                    <th className="text-left py-2 px-2 text-[hsl(215,20%,50%)] text-xs font-medium">Issue</th>
                    <th className="text-left py-2 px-2 text-[hsl(215,20%,50%)] text-xs font-medium">Priority</th>
                  </tr>
                </thead>
                <tbody>
                  {d.live_feed.slice(0, 15).map((inc, i) => (
                    <tr key={i} className="border-b border-[hsl(217,19%,18%)] hover:bg-[hsl(217,19%,15%)] transition-colors">
                      <td className="py-2 px-2 text-[hsl(215,20%,65%)] text-xs whitespace-nowrap">{inc.incident_date} {inc.incident_time?.slice(0, 5)}</td>
                      <td className="py-2 px-2 text-white text-xs">{inc.store_name}</td>
                      <td className="py-2 px-2 text-[hsl(215,20%,65%)] text-xs">{inc.location_label}</td>
                      <td className="py-2 px-2 text-[hsl(215,20%,65%)] text-xs">{inc.category_name}</td>
                      <td className="py-2 px-2"><PriorityBadge priority={inc.priority} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-sm text-[hsl(215,20%,50%)] text-center py-6">No recent incidents</p>}
        </CardContent>
      </Card>

      {/* Backfill button - only show when no data */}
      {d.total_all === 0 && (
        <div className="text-center py-4">
          <Button onClick={onBackfill} variant="outline" className="border-amber-500/50 text-amber-400" data-testid="backfill-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Import Historical Alert Data
          </Button>
        </div>
      )}
    </div>
  );
}

// =============================================
// INCIDENT LOG TAB
// =============================================
function LogTab({ incidents, total, page, pages, search, setSearch, priority, setPriority, status, setStatus, sort, order, onSort, onPageChange, onOpenDetail, loading, dateFrom, setDateFrom, dateTo, setDateTo, onApply }) {
  return (
    <div className="space-y-4" data-testid="log-tab">
      {/* Filters */}
      <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,22%)]">
        <CardContent className="p-3">
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-[10px] text-[hsl(215,20%,50%)] mb-1 block">Search</label>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[hsl(215,20%,50%)]" />
                <Input
                  data-testid="incident-search"
                  value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search incidents..."
                  className="pl-7 h-8 text-xs bg-[hsl(217,19%,8%)] border-[hsl(217,19%,27%)]"
                  onKeyDown={e => e.key === 'Enter' && onApply()}
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] text-[hsl(215,20%,50%)] mb-1 block">Priority</label>
              <Select value={priority} onValueChange={v => { setPriority(v); setTimeout(onApply, 100); }}>
                <SelectTrigger className="w-[110px] h-8 text-xs bg-[hsl(217,19%,8%)] border-[hsl(217,19%,27%)]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="HIGH">High (Red)</SelectItem>
                  <SelectItem value="MEDIUM">Medium (Orange)</SelectItem>
                  <SelectItem value="LOW">Low (Green)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] text-[hsl(215,20%,50%)] mb-1 block">Status</label>
              <Select value={status} onValueChange={v => { setStatus(v); setTimeout(onApply, 100); }}>
                <SelectTrigger className="w-[100px] h-8 text-xs bg-[hsl(217,19%,8%)] border-[hsl(217,19%,27%)]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="OPEN">Open</SelectItem>
                  <SelectItem value="CLOSED">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] text-[hsl(215,20%,50%)] mb-1 block">From</label>
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-8 text-xs bg-[hsl(217,19%,8%)] border-[hsl(217,19%,27%)] w-[130px]" data-testid="date-from" />
            </div>
            <div>
              <label className="text-[10px] text-[hsl(215,20%,50%)] mb-1 block">To</label>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-8 text-xs bg-[hsl(217,19%,8%)] border-[hsl(217,19%,27%)] w-[130px]" data-testid="date-to" />
            </div>
            <Button size="sm" onClick={onApply} className="h-8 bg-blue-600 hover:bg-blue-700" data-testid="apply-filters">
              <Filter className="w-3.5 h-3.5 mr-1" /> Apply
            </Button>
            <Button size="sm" variant="ghost" className="h-8 text-[hsl(215,20%,50%)]" onClick={() => { setSearch(''); setPriority('all'); setStatus('all'); setDateFrom(''); setDateTo(''); setTimeout(onApply, 100); }}>
              <X className="w-3.5 h-3.5 mr-1" /> Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results count */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-[hsl(215,20%,50%)]">{total} incident{total !== 1 ? 's' : ''} found</p>
        <p className="text-xs text-[hsl(215,20%,50%)]">Page {page} of {pages}</p>
      </div>

      {/* Table */}
      <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,22%)]">
        <CardContent className="p-0">
          {loading ? <LoadingState /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="incident-log-table">
                <thead>
                  <tr className="border-b border-[hsl(217,19%,22%)]">
                    {[
                      { key: 'incident_id', label: 'Incident ID' },
                      { key: 'incident_created_at', label: 'Date / Time' },
                      { key: 'store_name', label: 'Store' },
                      { key: null, label: 'Location' },
                      { key: 'category_name', label: 'Issue Type' },
                      { key: 'priority', label: 'Priority' },
                      { key: null, label: 'Photo' },
                      { key: 'incident_status', label: 'Status' },
                      { key: null, label: '' },
                    ].map((col, i) => (
                      <th key={i} className={`text-left py-2.5 px-3 text-[hsl(215,20%,50%)] text-xs font-medium ${col.key ? 'cursor-pointer hover:text-white transition-colors' : ''}`} onClick={() => col.key && onSort(col.key)}>
                        <div className="flex items-center space-x-1">
                          <span>{col.label}</span>
                          {col.key && sort === col.key && <ArrowUpDown className="w-3 h-3" />}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {incidents.length === 0 ? (
                    <tr><td colSpan={9} className="text-center py-8 text-[hsl(215,20%,50%)] text-sm">No incidents match your filters</td></tr>
                  ) : incidents.map((inc, i) => (
                    <tr key={i} className="border-b border-[hsl(217,19%,18%)] hover:bg-[hsl(217,19%,15%)] transition-colors cursor-pointer" onClick={() => onOpenDetail(inc.incident_id)}>
                      <td className="py-2 px-3 text-blue-400 text-xs font-mono">{inc.incident_id}</td>
                      <td className="py-2 px-3 text-[hsl(215,20%,65%)] text-xs whitespace-nowrap">{inc.incident_date}<br /><span className="text-[hsl(215,20%,45%)]">{inc.incident_time?.slice(0, 5)}</span></td>
                      <td className="py-2 px-3 text-white text-xs">{inc.store_name}</td>
                      <td className="py-2 px-3 text-[hsl(215,20%,65%)] text-xs">{inc.location_label}</td>
                      <td className="py-2 px-3 text-[hsl(215,20%,65%)] text-xs">{inc.category_name}</td>
                      <td className="py-2 px-3"><PriorityBadge priority={inc.priority} /></td>
                      <td className="py-2 px-3 text-xs">{inc.has_photo ? <span className="text-green-400">Yes</span> : <span className="text-[hsl(215,20%,40%)]">No</span>}</td>
                      <td className="py-2 px-3"><StatusBadge status={inc.incident_status} /></td>
                      <td className="py-2 px-3"><Eye className="w-3.5 h-3.5 text-[hsl(215,20%,40%)]" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-center space-x-2">
          <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => onPageChange(page - 1)} className="text-[hsl(215,20%,65%)]">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          {Array.from({ length: Math.min(pages, 7) }, (_, i) => {
            const p = page <= 4 ? i + 1 : page + i - 3;
            if (p < 1 || p > pages) return null;
            return (
              <Button key={p} size="sm" variant={p === page ? 'default' : 'ghost'} onClick={() => onPageChange(p)}
                className={p === page ? 'bg-blue-600' : 'text-[hsl(215,20%,65%)]'}>{p}</Button>
            );
          })}
          <Button size="sm" variant="ghost" disabled={page >= pages} onClick={() => onPageChange(page + 1)} className="text-[hsl(215,20%,65%)]">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

// =============================================
// ANALYTICS TAB
// =============================================
function AnalyticsTab({ analytics, loading, dateFrom, setDateFrom, dateTo, setDateTo, onApply }) {
  if (loading && !analytics) return <LoadingState />;
  if (!analytics) return null;

  const a = analytics;

  return (
    <div className="space-y-5" data-testid="analytics-tab">
      {/* Date Range Finder */}
      <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,22%)]">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-[hsl(215,20%,65%)] flex items-center space-x-2">
            <Calendar className="w-4 h-4" /><span>Date Range Incident Finder</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="text-[10px] text-[hsl(215,20%,50%)] mb-1 block">From</label>
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-8 text-xs bg-[hsl(217,19%,8%)] border-[hsl(217,19%,27%)] w-[150px]" />
            </div>
            <div>
              <label className="text-[10px] text-[hsl(215,20%,50%)] mb-1 block">To</label>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-8 text-xs bg-[hsl(217,19%,8%)] border-[hsl(217,19%,27%)] w-[150px]" />
            </div>
            <Button size="sm" onClick={onApply} className="h-8 bg-blue-600 hover:bg-blue-700">Apply Range</Button>
            <Button size="sm" variant="ghost" className="h-8 text-[hsl(215,20%,50%)]" onClick={() => { setDateFrom(''); setDateTo(''); setTimeout(onApply, 100); }}>Clear</Button>

            {/* Quick summary */}
            <div className="flex items-center space-x-4 ml-auto">
              <div className="text-center"><p className="text-lg font-bold text-white">{a.total}</p><p className="text-[10px] text-[hsl(215,20%,50%)]">Total</p></div>
              <div className="text-center"><p className="text-lg font-bold text-red-400">{a.red_count}</p><p className="text-[10px] text-[hsl(215,20%,50%)]">Red</p></div>
              <div className="text-center"><p className="text-lg font-bold text-orange-400">{a.orange_count}</p><p className="text-[10px] text-[hsl(215,20%,50%)]">Orange</p></div>
              <div className="text-center"><p className="text-lg font-bold text-green-400">{a.green_count}</p><p className="text-[10px] text-[hsl(215,20%,50%)]">Green</p></div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Trend line */}
      {a.daily_trend?.length > 0 && (
        <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,22%)]">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-[hsl(215,20%,65%)]">Daily Incident Trend</CardTitle></CardHeader>
          <CardContent className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={a.daily_trend} margin={{ left: 0, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(217,19%,22%)" />
                <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <Tooltip contentStyle={{ background: 'hsl(217,19%,15%)', border: '1px solid hsl(217,19%,27%)', color: '#f8fafc', fontSize: 12 }} />
                <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Analytics Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* By Category */}
        <ChartCard title="Incidents by Category" data={a.by_category} dataKey="count" nameKey="category" color="#3b82f6" layout="vertical" multiColor />

        {/* By Store */}
        <ChartCard title="Incidents by Store" data={a.by_store} dataKey="count" nameKey="store_name" color="#8b5cf6" layout="vertical" multiColor />

        {/* By Day of Week */}
        <ChartCard title="Incidents by Day of Week" data={a.by_day_of_week} dataKey="count" nameKey="day" color="#f97316" />

        {/* By Shift */}
        <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,22%)]">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-[hsl(215,20%,65%)]">Incidents by Shift</CardTitle></CardHeader>
          <CardContent className="h-[220px]">
            {a.by_shift?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={a.by_shift.map(s => ({ name: s.shift, value: s.count }))} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} label={({ name, value }) => `${name}: ${value}`}>
                    {a.by_shift.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: 'hsl(217,19%,15%)', border: '1px solid hsl(217,19%,27%)', color: '#f8fafc', fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11, color: '#94a3b8' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <EmptyChart />}
          </CardContent>
        </Card>

        {/* By Hour */}
        <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,22%)] lg:col-span-2">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-[hsl(215,20%,65%)]">Incidents by Hour of Day</CardTitle></CardHeader>
          <CardContent className="h-[220px]">
            {a.by_hour?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={a.by_hour.map(h => ({ hour: `${String(h.hour).padStart(2, '0')}:00`, count: h.count }))} margin={{ left: 0, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(217,19%,22%)" />
                  <XAxis dataKey="hour" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: 'hsl(217,19%,15%)', border: '1px solid hsl(217,19%,27%)', color: '#f8fafc', fontSize: 12 }} />
                  <Bar dataKey="count" fill="#14b8a6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyChart />}
          </CardContent>
        </Card>

        {/* By Location */}
        <ChartCard title="Top Hazard Locations" data={a.by_location} dataKey="count" nameKey="location" color="#ec4899" layout="vertical" />

        {/* Photo breakdown */}
        <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,22%)]">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-[hsl(215,20%,65%)]">Photo Attachment Rate</CardTitle></CardHeader>
          <CardContent className="h-[220px]">
            {a.total > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[{ name: 'With Photo', value: a.with_photo }, { name: 'Without Photo', value: a.without_photo }]}
                    dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75}
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    <Cell fill="#22c55e" />
                    <Cell fill="#64748b" />
                  </Pie>
                  <Tooltip contentStyle={{ background: 'hsl(217,19%,15%)', border: '1px solid hsl(217,19%,27%)', color: '#f8fafc', fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11, color: '#94a3b8' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <EmptyChart />}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// =============================================
// SAFETY SCORES TAB
// =============================================
function SafetyTab({ scores, loading, days, setDays, onRefresh }) {
  if (loading && !scores) return <LoadingState />;
  if (!scores) return null;

  const getScoreColor = (score) => {
    if (score >= 80) return '#22c55e';
    if (score >= 50) return '#f97316';
    return '#ef4444';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 50) return 'Needs Attention';
    return 'At Risk';
  };

  return (
    <div className="space-y-5" data-testid="safety-tab">
      <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,22%)]">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm text-[hsl(215,20%,65%)] flex items-center space-x-2">
              <Shield className="w-4 h-4 text-blue-400" /><span>Store Safety Score</span>
            </CardTitle>
            <div className="flex items-center space-x-2">
              <Select value={days.toString()} onValueChange={v => { setDays(parseInt(v)); setTimeout(onRefresh, 100); }}>
                <SelectTrigger className="w-[130px] h-8 text-xs bg-[hsl(217,19%,8%)] border-[hsl(217,19%,27%)]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="365">Last year</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-[hsl(215,20%,45%)] mb-4">
            Score = 100 - (Red x5 + Orange x3 + Green x1). Higher is safer.
          </p>
          {scores.scores?.length > 0 ? (
            <div className="space-y-3">
              {scores.scores.map((s, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-[hsl(217,19%,8%)] border border-[hsl(217,19%,18%)]" data-testid={`safety-score-${s.store_id}`}>
                  <div className="flex-1">
                    <p className="text-sm text-white font-medium">{s.store_name}</p>
                    <p className="text-xs text-[hsl(215,20%,50%)]">{s.store_city}</p>
                    <div className="flex items-center space-x-3 mt-1">
                      <span className="text-xs text-red-400">{s.high} High</span>
                      <span className="text-xs text-orange-400">{s.medium} Medium</span>
                      <span className="text-xs text-green-400">{s.low} Low</span>
                      <span className="text-xs text-[hsl(215,20%,50%)]">({s.total_incidents} total)</span>
                    </div>
                  </div>
                  <div className="text-right ml-4">
                    <p className="text-3xl font-bold" style={{ color: getScoreColor(s.safety_score) }}>{s.safety_score}</p>
                    <p className="text-xs" style={{ color: getScoreColor(s.safety_score) }}>{getScoreLabel(s.safety_score)}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-[hsl(215,20%,50%)] text-center py-6">No safety score data available</p>}
        </CardContent>
      </Card>
    </div>
  );
}

// =============================================
// INCIDENT DETAIL
// =============================================
function IncidentDetail({ incident }) {
  const i = incident;
  const rows = [
    ['Incident ID', i.incident_id],
    ['Date', i.incident_date],
    ['Time', i.incident_time],
    ['Store', i.store_name],
    ['City / Region', i.store_city],
    ['Location', i.location_label],
    ['Issue Type', i.category_name],
    ['Priority', `${i.priority} (${i.priority_color})`],
    ['Photo Attached', i.has_photo ? 'Yes' : 'No'],
    ['Notification Method', i.notification_method],
    ['Notification Sent', i.notification_sent ? 'Yes' : 'No'],
    ['Status', i.incident_status],
    ['Day of Week', i.day_of_week_name],
    ['Shift', i.shift_segment],
    ['Duplicate Reports', i.duplicate_reports_count > 1 ? `${i.duplicate_reports_count} reports` : 'Single report'],
    ['Comment', i.text_comment || '-'],
    ['Created', i.incident_created_at],
    ['Updated', i.incident_updated_at],
  ];

  return (
    <div className="space-y-1 max-h-[60vh] overflow-y-auto" data-testid="incident-detail">
      {rows.map(([label, value], idx) => (
        <div key={idx} className="flex items-start py-1.5 border-b border-[hsl(217,19%,18%)] last:border-0">
          <span className="text-xs text-[hsl(215,20%,50%)] w-[140px] flex-shrink-0">{label}</span>
          <span className="text-xs text-white flex-1">{value}</span>
        </div>
      ))}
    </div>
  );
}

// =============================================
// SHARED COMPONENTS
// =============================================
function ChartCard({ title, data, dataKey, nameKey, color, layout, multiColor }) {
  return (
    <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,22%)]">
      <CardHeader className="pb-2"><CardTitle className="text-sm text-[hsl(215,20%,65%)]">{title}</CardTitle></CardHeader>
      <CardContent className="h-[220px]">
        {data?.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            {layout === 'vertical' ? (
              <BarChart data={data} layout="vertical" margin={{ left: 10, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(217,19%,22%)" />
                <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <YAxis dataKey={nameKey} type="category" width={120} tick={{ fill: '#94a3b8', fontSize: 10 }} />
                <Tooltip contentStyle={{ background: 'hsl(217,19%,15%)', border: '1px solid hsl(217,19%,27%)', color: '#f8fafc', fontSize: 12 }} />
                <Bar dataKey={dataKey} radius={[0, 4, 4, 0]}>
                  {data.map((_, i) => <Cell key={i} fill={multiColor ? PIE_COLORS[i % PIE_COLORS.length] : color} />)}
                </Bar>
              </BarChart>
            ) : (
              <BarChart data={data} margin={{ left: 0, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(217,19%,22%)" />
                <XAxis dataKey={nameKey} tick={{ fill: '#94a3b8', fontSize: 10 }} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <Tooltip contentStyle={{ background: 'hsl(217,19%,15%)', border: '1px solid hsl(217,19%,27%)', color: '#f8fafc', fontSize: 12 }} />
                <Bar dataKey={dataKey} radius={[4, 4, 0, 0]}>
                  {data.map((_, i) => <Cell key={i} fill={multiColor ? PIE_COLORS[i % PIE_COLORS.length] : color} />)}
                </Bar>
              </BarChart>
            )}
          </ResponsiveContainer>
        ) : <EmptyChart />}
      </CardContent>
    </Card>
  );
}

function EmptyChart() {
  return <div className="h-full flex items-center justify-center text-[hsl(215,20%,40%)] text-sm">No data available</div>;
}

function LoadingState() {
  return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-blue-500 animate-spin" /></div>;
}
